import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ADD_SAMPLE_TRIGGER_EVENT,
  SAMPLE_TRIGGER_EVENT_DATA,
  TEST_USER,
  TEST_USER_TYPE,
  TestUserType,
} from './constants';

const schema = z.object({
  [ADD_SAMPLE_TRIGGER_EVENT]: z.boolean(),
  [SAMPLE_TRIGGER_EVENT_DATA]: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === '') return true; // Allow empty/optional values
        try {
          JSON.parse(value);
          return true;
        } catch {
          return false;
        }
      },
      {
        message: 'Please enter valid JSON',
      },
    ),
  [TEST_USER]: z.string().min(1, 'Please enter at least one user'),
  [TEST_USER_TYPE]: z.nativeEnum(TestUserType),
});

export type JourneyTestingProps = { testUser: string; testUserType: TestUserType };

export type JourneyTestingFormSchemaType = Required<z.infer<typeof schema>>;

/**
 * This form provider is created using [React Hook Form](https://react-hook-form.com/)
 * and [zod](https://zod.dev/) for schema validation
 */
export const JourneyTestingFormProvider: FC<PropsWithChildren<JourneyTestingProps>> = ({
  children,
  testUser,
  testUserType,
}) => {
  const defaultValues = {
    [ADD_SAMPLE_TRIGGER_EVENT]: false,
    [SAMPLE_TRIGGER_EVENT_DATA]: '',
    [TEST_USER]: testUser,
    [TEST_USER_TYPE]: testUserType,
  };

  const methods = useForm<JourneyTestingFormSchemaType>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
